import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Spinner,
  Badge,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import instance from '../../api/Axios';

const BursaryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bursary, setBursary] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('identification');
  
  // Component management state
  const [componentTypes, setComponentTypes] = useState([]);
  const [bursaryComponents, setBursaryComponents] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentError, setComponentError] = useState('');

  // Ensure beneficiaries is always an array
  const safeBeneficiaries = useMemo(() => {
    return Array.isArray(beneficiaries) ? beneficiaries : [];
  }, [beneficiaries]);

  const handleAddBeneficiary = () => {
    navigate(`/general-dashboard/bursaries/${id}/add-beneficiary`);
  };

  // Debug useEffect to log beneficiaries state changes
  useEffect(() => {
    console.log('Beneficiaries state changed:', beneficiaries);
    console.log('Beneficiaries type:', typeof beneficiaries);
    console.log('Is beneficiaries array?', Array.isArray(beneficiaries));
    console.log('Safe beneficiaries:', safeBeneficiaries);
  }, [beneficiaries, safeBeneficiaries]);

  useEffect(() => {
    const fetchBursary = async () => {
      try {
        setLoading(true);
        setError('');
        console.log('=== FETCHING BURSARY DETAIL ===');
        console.log('Bursary ID:', id);
        
        const response = await instance.get(`/api/bursaries/${id}/`);
        console.log('Bursary detail response:', response.data);
        setBursary(response.data);
      } catch (error) {
        console.error('Error fetching bursary detail:', error);
        if (error.response?.status === 404) {
          setError('Bursary not found.');
        } else {
          setError('Failed to fetch bursary details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBursary();
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'ZAR',
    });
    return formatter.format(amount);
  };

  const fetchBeneficiaries = async () => {
    if (!id) return;
    
    setBeneficiariesLoading(true);
    try {
      const response = await instance.get(`/api/accounts/bursaries/${id}/beneficiaries/`);
      console.log('Beneficiaries response:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is response.data an array?', Array.isArray(response.data));
      
      // Handle different response formats
      let beneficiariesData = [];
      if (Array.isArray(response.data)) {
        beneficiariesData = response.data;
      } else if (response.data && Array.isArray(response.data.beneficiaries)) {
        beneficiariesData = response.data.beneficiaries;
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        beneficiariesData = response.data.results;
      }
      
      console.log('Setting beneficiaries to:', beneficiariesData);
      setBeneficiaries(beneficiariesData);
    } catch (error) {
      console.error('Error fetching beneficiaries:', error);
      setBeneficiaries([]); // Ensure it's always an array
    } finally {
      setBeneficiariesLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Fetch beneficiaries when the tab is activated for the first time
    if (tab === 'beneficiaries' && safeBeneficiaries.length === 0 && !beneficiariesLoading) {
      fetchBeneficiaries();
    }
    // Fetch component data when the components tab is activated for the first time
    if (tab === 'components' && componentTypes.length === 0 && !componentsLoading) {
      fetchComponentTypes();
    }
  };

  // Beneficiary action handlers
  const handleViewBeneficiary = (beneficiary) => {
    console.log('View beneficiary:', beneficiary);
    // Navigate to beneficiary detail page
    navigate('/general-dashboard/beneficiaries/view/' + beneficiary.id);
  };

  const handleViewEntitlements = (beneficiary) => {
    console.log('View entitlements for beneficiary:', beneficiary);
    // Navigate to beneficiary entitlements page
    navigate('/general-dashboard/beneficiaries/entitlements/' + beneficiary.id);
  };

  const handleGenerateVouchers = (beneficiary) => {
    console.log('Generate vouchers for beneficiary:', beneficiary);
    // Navigate to voucher generation page
    navigate('/general-dashboard/beneficiaries/generate-vouchers/' + beneficiary.id);
  };

  // Component management functions
  const fetchComponentTypes = async () => {
    try {
      setComponentsLoading(true);
      setComponentError('');
      
      // Fetch all available component types
      const typesResponse = await instance.get('/api/component-types/');
      setComponentTypes(typesResponse.data);
      
      // Fetch components already assigned to this bursary
      const componentsResponse = await instance.get(`/api/accounts/bursaries/${id}/components/`);
      setBursaryComponents(componentsResponse.data);
      
    } catch (error) {
      console.error('Error fetching component data:', error);
      setComponentError('Failed to load component information.');
    } finally {
      setComponentsLoading(false);
    }
  };

  const toggleComponent = async (componentType, isChecked) => {
    try {
      if (isChecked) {
        // Add component to bursary
        const payload = {
          bursary: parseInt(id),
          component_type: componentType.id,
          name: componentType.name,
          description: componentType.description,
          is_active: true
        };
        
        const response = await instance.post('/api/accounts/components/', payload);
        setBursaryComponents(prev => [...prev, response.data]);
        
      } else {
        // Remove component from bursary
        const existingComponent = bursaryComponents.find(
          comp => comp.component_type === componentType.id
        );
        
        if (existingComponent) {
          await instance.delete(`/api/accounts/components/${existingComponent.id}/`);
          setBursaryComponents(prev => 
            prev.filter(comp => comp.component_type !== componentType.id)
          );
        }
      }
    } catch (error) {
      console.error('Error toggling component:', error);
      setComponentError('Failed to update component. Please try again.');
      
      // Clear error after 5 seconds
      setTimeout(() => setComponentError(''), 5000);
    }
  };

  const isComponentSelected = (componentTypeId) => {
    return bursaryComponents.some(comp => comp.component_type === componentTypeId);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Loading bursary details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button 
            color="secondary" 
            onClick={() => navigate('/general-dashboard/bursaries')}
            className="d-flex align-items-center"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Bursaries
          </Button>
        </div>
        <Alert color="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      </div>
    );
  }

  if (!bursary) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Button 
            color="secondary" 
            onClick={() => navigate('/general-dashboard/bursaries')}
            className="d-flex align-items-center"
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Bursaries
          </Button>
        </div>
        <Alert color="warning">
          <i className="fas fa-info-circle me-2"></i>
          Bursary not found.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button 
          color="secondary" 
          onClick={() => navigate('/general-dashboard/bursaries')}
          className="d-flex align-items-center"
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Bursaries
        </Button>
        <Button 
          color="primary" 
          onClick={() => navigate(`/general-dashboard/bursaries/edit/${bursary.id}`)}
          className="d-flex align-items-center"
        >
          <i className="fas fa-edit me-2"></i>
          Edit Bursary
        </Button>
      </div>

      <Card>
        <CardHeader className="bg-primary text-white">
          <div className="d-flex align-items-center">
            <i className="fas fa-graduation-cap me-3"></i>
            <div>
              <h4 className="mb-0">Bursary Details</h4>
              <small>{bursary.name}</small>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === 'identification' ? 'active' : ''}
                onClick={() => handleTabChange('identification')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-id-card me-2"></i>
                Bursary Identification
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'budget' ? 'active' : ''}
                onClick={() => handleTabChange('budget')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-calculator me-2"></i>
                Budget
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'components' ? 'active' : ''}
                onClick={() => handleTabChange('components')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-puzzle-piece me-2"></i>
                Bursary Components
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'beneficiaries' ? 'active' : ''}
                onClick={() => handleTabChange('beneficiaries')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-users me-2"></i>
                Beneficiaries
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'terms' ? 'active' : ''}
                onClick={() => handleTabChange('terms')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-file-contract me-2"></i>
                Terms and Conditions
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'documents' ? 'active' : ''}
                onClick={() => handleTabChange('documents')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-folder-open me-2"></i>
                Bursary Documents
              </NavLink>
            </NavItem>
          </Nav>
          
          <TabContent activeTab={activeTab}>
            <TabPane tabId="identification">
              <div className="mt-4">
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <strong>Funder Name:</strong>
                      <p className="text-muted">{bursary.funder_name || '-'}</p>
                    </div>
                    <div className="mb-3">
                      <strong>BMO Bursary Code:</strong>
                      <p className="text-muted">{bursary.bmo_bursary_code || '-'}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Bursary Name:</strong>
                      <p className="text-muted">{bursary.name}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Start Date:</strong>
                      <p className="text-muted">{formatDate(bursary.start_date)}</p>
                    </div>
                    <div className="mb-3">
                      <strong>End Date:</strong>
                      <p className="text-muted">{formatDate(bursary.end_date)}</p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <strong>Bursary Currency:</strong>
                      <p>
                        <Badge color="info" pill>
                          {bursary.bursary_currency || 'ZAR'}
                        </Badge>
                      </p>
                    </div>
                    <div className="mb-3">
                      <strong>Funding Amount:</strong>
                      <p className="text-muted">{formatCurrency(bursary.funding_amount, bursary.bursary_currency)}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Number of Beneficiaries:</strong>
                      <p>
                        <Badge color="success" pill>
                          {bursary.number_of_beneficiaries || 0}
                        </Badge>
                      </p>
                    </div>
                    <div className="mb-3">
                      <strong>Bursary Manager:</strong>
                      <p className="text-muted">{bursary.bursary_manager || '-'}</p>
                    </div>
                  </Col>
                </Row>
                <Row className="mt-4 pt-3 border-top">
                  <Col md="12">
                    <div className="d-flex justify-content-between">
                      <small className="text-muted">
                        <strong>Created By:</strong> {bursary.created_by_name || '-'}
                      </small>
                      <small className="text-muted">
                        <strong>Created At:</strong> {formatDate(bursary.created_at)}
                      </small>
                    </div>
                  </Col>
                </Row>
              </div>
            </TabPane>
            
            <TabPane tabId="budget">
              <div className="mt-4">
                <div className="text-center text-muted">
                  <i className="fas fa-calculator fa-3x mb-3"></i>
                  <p>Budget information will be displayed here</p>
                </div>
              </div>
            </TabPane>
            
            <TabPane tabId="components">
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">
                    <i className="fas fa-puzzle-piece me-2"></i>
                    Bursary Components
                  </h5>
                  <small className="text-muted">
                    Select the components included in this bursary
                  </small>
                </div>

                {componentError && (
                  <Alert color="danger" className="mb-4">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {componentError}
                  </Alert>
                )}

                {componentsLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading component types...</p>
                  </div>
                ) : componentTypes.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="fas fa-puzzle-piece fa-3x mb-3"></i>
                    <h5>No Component Types Available</h5>
                    <p>Contact your administrator to add component types that can be used in bursaries.</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted mb-4">
                      Check the boxes below to include components in this bursary. 
                      Students will have access to the selected components.
                    </p>
                    
                    <Row>
                      {componentTypes.map((componentType) => {
                        const isSelected = isComponentSelected(componentType.id);
                        return (
                          <Col md={6} lg={4} key={componentType.id} className="mb-3">
                            <Card 
                              className={`h-100 ${isSelected ? 'border-success' : 'border-light'}`}
                              style={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => toggleComponent(componentType, !isSelected)}
                            >
                              <CardBody className="d-flex align-items-start">
                                <div className="me-3 mt-1">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isSelected}
                                    onChange={(e) => toggleComponent(componentType, e.target.checked)}
                                    style={{ pointerEvents: 'none' }}
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-2 d-flex align-items-center">
                                    {componentType.name}
                                    <Badge 
                                      color={componentType.is_active ? 'success' : 'warning'} 
                                      className="ms-2"
                                      pill
                                    >
                                      {componentType.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                  </h6>
                                  <small className="text-muted">
                                    Code: {componentType.code}
                                  </small>
                                  {componentType.description && (
                                    <p className="small mb-0 mt-2">
                                      {componentType.description}
                                    </p>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>

                    <div className="mt-4 p-3 bg-light rounded">
                      <h6 className="mb-2">
                        <i className="fas fa-info-circle me-2 text-info"></i>
                        Selected Components Summary
                      </h6>
                      {bursaryComponents.length === 0 ? (
                        <small className="text-muted">
                          No components selected for this bursary.
                        </small>
                      ) : (
                        <div>
                          <small className="text-muted">
                            This bursary includes {bursaryComponents.length} component(s):
                          </small>
                          <div className="mt-2">
                            {bursaryComponents.map((component, index) => {
                              const componentType = componentTypes.find(ct => ct.id === component.component_type);
                              return (
                                <Badge key={index} color="success" className="me-2 mb-2">
                                  {componentType?.name || component.name}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabPane>
            
            <TabPane tabId="beneficiaries">
              <div className="mt-4">
                {/* BMO and Funder Information Table */}
                <div className="mb-4">
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: '150px' }}>BMO</td>
                          <td className="bg-secondary bg-opacity-25" style={{ width: '200px' }}>{bursary?.bmo_bursary_code || '-'}</td>
                          <td className="bg-secondary bg-opacity-25">{bursary?.bmo_name || '-'}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Funder Code</td>
                          <td className="bg-secondary bg-opacity-25">{bursary?.funder_code || '-'}</td>
                          <td className="bg-secondary bg-opacity-25">{bursary?.funder_name || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bursary Summary Table */}
                <div className="mb-4">
                  <h5 className="mb-3">
                    <i className="fas fa-chart-bar me-2"></i>
                    Bursary Overview
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>System Bursary Code</th>
                          <th>BMO Bursary Code</th>
                          <th>Bursary Name</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Total Financial Value</th>
                          <th>All Time Count</th>
                          <th>Current</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <Badge color="primary" className="font-monospace">
                              {bursary.bursary_code || '-'}
                            </Badge>
                          </td>
                          <td>
                            <span className="text-muted">
                              {bursary.bmo_bursary_code || '-'}
                            </span>
                          </td>
                          <td>
                            <strong>{bursary.name}</strong>
                          </td>
                          <td>{formatDate(bursary.start_date)}</td>
                          <td>{formatDate(bursary.end_date)}</td>
                          <td>
                            <strong className="text-success">
                              {formatCurrency(bursary.funding_amount, bursary.bursary_currency)}
                            </strong>
                          </td>
                          <td>
                            <Badge color="info" pill>
                              {bursary.all_time_beneficiaries_count || 0}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="success" pill>
                              {bursary.current_active_beneficiaries_count || 0}
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Beneficiaries List */}
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <i className="fas fa-users me-2"></i>
                      Beneficiaries
                    </h5>
                    <button 
                      type="button" 
                      className="d-flex align-items-center btn btn-primary"
                      onClick={handleAddBeneficiary}
                    >
                      <i className="fas fa-plus me-2"></i>
                      ADD +
                    </button>
                  </div>
                  
                  <div className="table-responsive" style={{ minHeight: '400px' }}>
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Surname</th>
                          <th>First Name / Other</th>
                          <th>Institution</th>
                          <th>Field of Study</th>
                          <th>Student Number</th>
                          <th>National ID</th>
                          <th>Gender</th>
                          <th>Entry Date</th>
                          <th>Exit Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficiariesLoading ? (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              <Spinner size="sm" className="me-2" />
                              Loading beneficiaries...
                            </td>
                          </tr>
                        ) : safeBeneficiaries.length === 0 ? (
                          <tr>
                            <td colSpan="10" className="text-center text-muted py-4">
                              <i className="fas fa-users fa-2x mb-2"></i>
                              <br />
                              No beneficiaries found
                            </td>
                          </tr>
                        ) : (
                          safeBeneficiaries.map((beneficiary) => (
                            <tr key={beneficiary.id}>
                              <td>{beneficiary.surname}</td>
                              <td>{beneficiary.first_name_other}</td>
                              <td>{beneficiary.institution_name || '-'}</td>
                              <td>{beneficiary.field_of_study || '-'}</td>
                              <td>{beneficiary.student_number || '-'}</td>
                              <td>{beneficiary.national_id}</td>
                              <td>{beneficiary.gender || '-'}</td>
                              <td>{formatDate(beneficiary.entry_date)}</td>
                              <td>{formatDate(beneficiary.exit_date)}</td>
                              <td>
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    caret={false}
                                    className="btn btn-outline-primary btn-sm"
                                    aria-haspopup="true"
                                  >
                                    ⋯
                                  </DropdownToggle>
                                  <DropdownMenu right>
                                    <DropdownItem onClick={() => handleViewBeneficiary(beneficiary)}>
                                      <i className="fas fa-eye me-2"></i>
                                      View
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleViewEntitlements(beneficiary)}>
                                      <i className="fas fa-list-check me-2"></i>
                                      Entitlements
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleGenerateVouchers(beneficiary)}>
                                      <i className="fas fa-ticket-alt me-2"></i>
                                      Generate Vouchers
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabPane>
            
            <TabPane tabId="terms">
              <div className="mt-4">
                <div className="text-center text-muted">
                  <i className="fas fa-file-contract fa-3x mb-3"></i>
                  <p>Terms and conditions information will be displayed here</p>
                </div>
              </div>
            </TabPane>
            
            <TabPane tabId="documents">
              <div className="mt-4">
                <div className="text-center text-muted">
                  <i className="fas fa-folder-open fa-3x mb-3"></i>
                  <p>Bursary documents will be displayed here</p>
                </div>
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default BursaryDetail;